import React, { useEffect, useState } from "react";
import { get, omit } from "lodash";
import moment from "moment";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useStores } from "_common/hooks";
import { useForm } from "react-hook-form";

import { observer } from "mobx-react";
import { NotificationManager } from "react-notifications";
import { ProfileApi } from "states/api";
import UploadFile from "_common/component/UploadFile";
import i18next from "i18next";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";

import UploadKYC from "_common/component/UploadKYC";
import Radio from "_common/component/Radio";
import HeaderTop from "_common/component/Layout/Header/HeaderTop";

export default function KYCStep2() {
  const navigate = useNavigate();
  const {
    authStore: { user },
  } = useStores();
  const queryClient = useQueryClient();
  const [front_images, setFrontImage] = useState();
  const [back_images, setBackImage] = useState();
  const [self_images, setSelfImage] = useState();

  const schema = yup.object({}).required();

  const { mutate, isLoading } = useMutation(
    (variables) => ProfileApi.update({ params: variables }),
    {
      onSuccess: (res) => {
        navigate("/member");
        queryClient.invalidateQueries(["get_profile"]);
        NotificationManager.success(
          i18next.t(res?.msg) || i18next.t("action_success_msg")
        );
      },
      onError: (error) => {
        const errorMessage =
          i18next.t(error?.message) ?? i18next.t("action_failed_msg");
        NotificationManager.error(errorMessage);
      },
    }
  );

  const {
    register,
    trigger,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSave = (values) => {
    if (isLoading) return null;
    if (!front_images)
      return NotificationManager.warning(
        "Vui lòng tải lên file CMND/CCCD mặt trước"
      );
    if (!back_images)
      return NotificationManager.warning(
        "Vui lòng tải lên file CMND/CCCD mặt sau"
      );
    trigger();
    const formValue = getValues();
    const isValid = schema.isValidSync(formValue);
    if (isValid) {
      mutate({
        ...formValue,
        front_image: front_images,
        back_image: back_images,
        avatar: self_images,
      });
    }
    return null;
  };

  const onReceiveFrontImages = (images) => {
    setFrontImage(images[0]?.url);
  };

  const onReceiveBackImages = (images) => {
    setBackImage(images[0]?.url);
  };

  const onReceiveSelfImages = (images) => {
    setSelfImage(images[0]?.url);
  };

  return (
    <div className="container !mt-[70px]">
      <HeaderTop
        title={i18next.t("kyc_title")}
        onBack={() => navigate("/member/kyc")}
      />
      <form
        onSubmit={handleSubmit(onSave)}
        className="flex flex-col  px-4 my-4"
      >
        <div className="py-[6px] px-4 flex items-center bg-[#fff4e5] rounded mb-4">
          <div className="py-2 text-[#663c00] ">{i18next.t("note17")}</div>
        </div>
        <div className="flex flex-wrap gap-5" role="radiogroup">
          <Radio value="id_card">
            <span className="ml-2 text-[16px] text-white">
              {i18next.t("card_id")}
            </span>
          </Radio>
          <Radio value="passport">
            <span className="ml-2 text-[16px] text-white">
              {i18next.t("passport")}
            </span>
          </Radio>
          <Radio value="driving_license">
            <span className="ml-2 text-[16px] text-white">
              {i18next.t("driving_license")}
            </span>
          </Radio>
          <Radio value="residence_permit">
            <span className="ml-2 text-[16px] text-white">
              {i18next.t("residence_permit")}
            </span>
          </Radio>
        </div>
        <div className="mt-5">
          <UploadKYC
            onReceiveImages={onReceiveFrontImages}
            image={front_images}
            folder="kyc"
          >
            <img
              className="max-w-[80%]"
              src={
                front_images ||
                "https://api.demo92.apptestlive.com/assets/images/id.png"
              }
              alt=""
            />
            <div className="flex items-center w-fit gap-2 text-[#fff]">
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 24 24"
                height="20px"
                width="20px"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M9.715 12c1.151 0 2-.849 2-2s-.849-2-2-2-2 .849-2 2 .848 2 2 2z" />
                <path d="M20 4H4c-1.103 0-2 .841-2 1.875v12.25C2 19.159 2.897 20 4 20h16c1.103 0 2-.841 2-1.875V5.875C22 4.841 21.103 4 20 4zm0 14-16-.011V6l16 .011V18z" />
                <path
                  stroke="#3D5AFE"
                  fill="#3D5AFE"
                  d="M14 9h4v2h-4zm1 4h3v2h-3zm-1.57 2.536c0-1.374-1.676-2.786-3.715-2.786S6 14.162 6 15.536V16h7.43v-.464z"
                />
              </svg>
              <span className="text-[#3D5AFE] ">{i18next.t("note23")}</span>
            </div>
          </UploadKYC>
        </div>
        <div className="mt-2">
          <UploadKYC
            onReceiveImages={onReceiveBackImages}
            image={back_images}
            folder="kyc"
          >
            <img
              className="max-w-[80%]"
              src={
                back_images ||
                "https://api.demo92.apptestlive.com/assets/images/id.png"
              }
              alt=""
            />
            <div className="flex items-center w-fit gap-2 text-[#fff]">
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 24 24"
                height="20px"
                width="20px"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M9.715 12c1.151 0 2-.849 2-2s-.849-2-2-2-2 .849-2 2 .848 2 2 2z" />
                <path d="M20 4H4c-1.103 0-2 .841-2 1.875v12.25C2 19.159 2.897 20 4 20h16c1.103 0 2-.841 2-1.875V5.875C22 4.841 21.103 4 20 4zm0 14-16-.011V6l16 .011V18z" />
                <path
                  stroke="#3D5AFE"
                  fill="#3D5AFE"
                  d="M14 9h4v2h-4zm1 4h3v2h-3zm-1.57 2.536c0-1.374-1.676-2.786-3.715-2.786S6 14.162 6 15.536V16h7.43v-.464z"
                />
              </svg>
              <span className="text-[#3D5AFE] ">{i18next.t("note24")}</span>
            </div>
          </UploadKYC>
        </div>
        <div className="mt-2">
          <UploadKYC
            onReceiveImages={onReceiveSelfImages}
            image={self_images}
            folder="kyc"
          >
            <img
              className="max-w-[80%]"
              src={
                self_images ||
                "https://api.demo92.apptestlive.com/assets/images/selfie.webp"
              }
              alt=""
            />
            <div className="flex items-center w-fit gap-2 text-[#fff]">
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 16 16"
                height="20px"
                width="20px"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M11 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h6zM5 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H5z" />
                <path
                  stroke="#3D5AFE"
                  fill="#3D5AFE"
                  d="M8 14a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
                />
              </svg>
              <span className="text-[#3D5AFE] ">{i18next.t("note25")}</span>
            </div>
          </UploadKYC>
        </div>

        <div className="w-full mt-6">
          <button
            className="flex items-center gap-2 justify-center w-full px-6 py-3 bg-[#3d5afe] text-white    text-sm text-center text-medium rounded"
            type="submit"
          >
            {i18next.t("send")}
          </button>
        </div>
      </form>
    </div>
  );
}
